import { useEffect, useRef, useState } from "react";
import { MessageRenderer } from "../../../../components/MessageWithTableRenderer";

/* --- status vocabulary ------------------------------------------------ */
const STATUSES = {
  pending: {
    label: "Pending",
    dot: "bg-amber-400",
    chip: "bg-amber-100 text-amber-700 dark:bg-amber-400/20 dark:text-amber-300",
    accent: "border-l-amber-400",
  },
  reviewing: {
    label: "Reviewing",
    dot: "bg-blue-500",
    chip: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300",
    accent: "border-l-blue-500",
  },
  resolved: {
    label: "Resolved",
    dot: "bg-green-500",
    chip: "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300",
    accent: "border-l-green-500",
  },
};

/* --- failure categories ---------------------------------------------- */
const CATEGORIES = {
  wrong_answer: "Wrong answer",
  hallucination: "Hallucination",
  missing_knowledge: "Missing knowledge",
  misunderstood: "Misunderstood",
  tone: "Tone / robotic",
  latency: "Latency",
  config: "Config / setup",
  other: "Other",
};

/* --- helpers ---------------------------------------------------------- */
const fmtClock = (ms) =>
  new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const timeAgo = (ms) => {
  const mins = Math.floor((Date.now() - ms) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

/* --- copy-to-clipboard id pill --------------------------------------- */
function IdPill({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  return (
    <button
      onClick={copy}
      title={`${label}: ${value} (click to copy)`}
      className="group inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-xs text-gray-500 dark:text-gray-400 hover:border-orange-300 dark:hover:border-orange-500/50 transition-colors"
    >
      <span className="text-gray-300 dark:text-gray-500 uppercase tracking-wide">
        {label}
      </span>
      <span className="font-mono text-gray-600 dark:text-gray-300">
        {value}
      </span>
      <svg
        className="w-3 h-3 text-gray-300 group-hover:text-orange-400 transition-colors"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        {copied ? (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        ) : (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        )}
      </svg>
    </button>
  );
}

/* --- activity timeline entry ----------------------------------------- */
function ActivityEntry({ entry }) {
  if (entry.type === "note") {
    return (
      <div className="flex gap-2.5">
        <div className="mt-1 w-7 h-7 shrink-0 rounded-full bg-orange-100 dark:bg-orange-500/20 flex items-center justify-center text-xs font-bold text-orange-600 dark:text-orange-300">
          {(entry.author || "?").charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">
              {entry.author}
            </span>
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {timeAgo(entry.timestamp)}
            </span>
          </div>
          <div className="mt-1 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 leading-relaxed break-words">
            {entry.text}
          </div>
        </div>
      </div>
    );
  }
  // status / category change — a compact log line
  const label =
    entry.type === "status"
      ? `moved to ${STATUSES[entry.to]?.label || entry.to}`
      : `set category to ${CATEGORIES[entry.to] || entry.to}`;
  return (
    <div className="flex gap-2.5 items-center">
      <div className="w-7 h-7 shrink-0 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
        <svg
          className="w-3.5 h-3.5 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 5l7 7-7 7M5 5l7 7-7 7"
          />
        </svg>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        <span className="font-semibold text-gray-700 dark:text-gray-200">
          {entry.author}
        </span>{" "}
        {label} ·{" "}
        <span className="text-gray-400">{timeAgo(entry.timestamp)}</span>
      </p>
    </div>
  );
}

/* --- conversation modal ---------------------------------------------- */
export default function FeedbackModal({
  item,
  authorName,
  conversation,
  userHistory,
  expandedHistorySessionId,
  historySessionDetails,
  onToggleHistorySession,
  onLoadMoreHistory,
  onSaveAssessment,
  onAddNote,
  onDelete,
  onRetryConversation,
  onClose,
}) {
  const flaggedRef = useRef(null);
  const [draft, setDraft] = useState("");
  const st = STATUSES[item.status];
  const isGeneral = item.kind === "general";

  // Status + category are staged locally; nothing hits the API until Save.
  const [draftStatus, setDraftStatus] = useState(item.status);
  const [draftCategory, setDraftCategory] = useState(item.category || null);
  const [saving, setSaving] = useState(false);

  // Re-sync drafts whenever the persisted item changes (open, or after a save).
  useEffect(() => {
    setDraftStatus(item.status);
    setDraftCategory(item.category || null);
  }, [item.feedback_id, item.status, item.category]);

  const dirty =
    draftStatus !== item.status ||
    (draftCategory || null) !== (item.category || null);

  const saveAssessment = async () => {
    const patch = {};
    if (draftStatus !== item.status) patch.status = draftStatus;
    if ((draftCategory || null) !== (item.category || null))
      patch.category = draftCategory;
    if (!Object.keys(patch).length) return;

    setSaving(true);
    try {
      await onSaveAssessment(item.feedback_id, patch);
    } catch {
      // parent already surfaced the error; keep the unsaved state
    } finally {
      setSaving(false);
    }
  };

  const discardAssessment = () => {
    setDraftStatus(item.status);
    setDraftCategory(item.category || null);
  };

  const convLoading = conversation?.loading;
  const convError = conversation?.error;
  const messages = conversation?.messages || [];
  const flaggedAgentId = conversation?.flaggedAgentId || item.message_id;
  const flaggedUserId = conversation?.flaggedUserId || null;
  const isFlagged = (m) => m.id === flaggedAgentId || m.id === flaggedUserId;

  const timeline = [...(item.activity || [])].sort(
    (a, b) => a.timestamp - b.timestamp,
  );

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  // Scroll to the flagged exchange once the conversation has loaded.
  useEffect(() => {
    if (convLoading || !messages.length) return;
    const t = setTimeout(
      () =>
        flaggedRef.current?.scrollIntoView({
          block: "center",
          behavior: "smooth",
        }),
      120,
    );
    return () => clearTimeout(t);
  }, [convLoading, messages.length]);

  const submitNote = () => {
    const text = draft.trim();
    if (!text) return;
    onAddNote(item.feedback_id, text);
    setDraft("");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="w-full max-w-6xl h-[90vh] flex flex-col bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden animate-scaleIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-700 shrink-0">
          <div className="min-w-0 flex flex-col gap-1">
            <h2 className="text-base font-bold text-gray-800 dark:text-white">
              {isGeneral ? "Feedback report" : "Conversation session"}
            </h2>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span
                className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${st.chip}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                {st.label}
              </span>
              {!isGeneral && <IdPill label="session" value={item.session_id} />}
            </div>
            <div className="flex items-center gap-2.5 mt-2.5">
              <IdPill label="uid" value={item.user_id} />
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => onDelete(item.feedback_id)}
              title="Delete feedback"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-500 transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* body: conversation (left) + assessment (right) */}
        <div className="flex-1 min-h-0 flex flex-col md:flex-row">
          {/* conversation column */}
          <div className="w-[70%] min-h-0 flex flex-col md:border-r border-gray-100 dark:border-gray-700">
            {/* reason banner */}
            <div className="px-5 py-3 bg-orange-50 dark:bg-orange-500/10 border-b border-orange-100 dark:border-orange-500/20 shrink-0">
              <div className="flex items-start gap-2">
                <svg
                  className="w-4 h-4 text-orange-500 mt-0.5 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01M5.07 19H19a2 2 0 001.75-2.97l-6.93-12a2 2 0 00-3.5 0l-6.93 12A2 2 0 005.07 19z"
                  />
                </svg>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-orange-600 dark:text-orange-300 uppercase tracking-wide">
                    {isGeneral ? "Subject" : "User's reason for feedback"}
                  </p>
                  <p className="text-sm text-gray-800 dark:text-orange-100 mt-0.5">
                    “{item.reason}”
                  </p>
                </div>
              </div>
            </div>

            {isGeneral ? (
              /* feedback-form details — no chat session behind this report */
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-gray-50/60 dark:bg-gray-900/30">
                <div>
                  <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">
                    Category
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-200">
                    {item.category || "Uncategorized"}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">
                    Message
                  </p>
                  <div className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-3.5 py-2.5 text-sm text-gray-700 dark:text-gray-200 leading-relaxed whitespace-pre-wrap break-words">
                    {item.message}
                  </div>
                </div>
                {item.screenshots?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">
                      Screenshots
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {item.screenshots.map((s, i) => (
                        <a
                          key={s.url || i}
                          href={s.url}
                          target="_blank"
                          rel="noreferrer"
                          className="block rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 hover:opacity-90 transition-opacity"
                        >
                          <img
                            src={s.url}
                            alt={s.filename || `Screenshot ${i + 1}`}
                            className="w-full h-28 object-cover"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* recent chat history — there's no session tied to this report,
                    so this is the only way to give devs prior-usage context */}
                <div>
                  <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">
                    Recent conversations
                  </p>
                  {userHistory?.loading ? (
                    <p className="text-sm text-gray-400 py-2">Loading…</p>
                  ) : userHistory?.error ? (
                    <p className="text-sm text-gray-400 py-2">
                      Couldn't load conversation history.
                    </p>
                  ) : !userHistory?.items?.length ? (
                    <p className="text-sm text-gray-400 py-2">
                      No past conversations found for this user.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {userHistory.items.map((h) => {
                        const isSessionExpanded =
                          expandedHistorySessionId === h.session_id;
                        const detail = historySessionDetails?.[h.session_id];
                        return (
                          <div
                            key={h.session_id}
                            className={`rounded-lg bg-white dark:bg-gray-800 border overflow-hidden transition-colors ${
                              isSessionExpanded
                                ? "border-orange-300 dark:border-orange-500/50"
                                : "border-gray-200 dark:border-gray-700"
                            }`}
                          >
                            <button
                              onClick={() =>
                                onToggleHistorySession?.(h.session_id)
                              }
                              className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
                            >
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="text-xs text-gray-400 dark:text-gray-500">
                                  {timeAgo(h.created_at)} · {h.message_count}{" "}
                                  messages
                                </span>
                                <svg
                                  className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform ${
                                    isSessionExpanded
                                      ? "rotate-90 text-orange-500"
                                      : ""
                                  }`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                  />
                                </svg>
                              </div>
                              {h.user_preview && (
                                <p className="text-xs text-gray-600 dark:text-gray-300 truncate">
                                  “{h.user_preview}”
                                </p>
                              )}
                            </button>

                            {isSessionExpanded && (
                              <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-3 py-3 space-y-2.5 max-h-108 overflow-y-auto">
                                {detail?.loading ? (
                                  <p className="text-xs text-gray-400 py-2">
                                    Loading…
                                  </p>
                                ) : detail?.error ? (
                                  <p className="text-xs text-gray-400 py-2">
                                    Couldn't load this conversation.
                                  </p>
                                ) : (
                                  (detail?.messages || []).map((m, i) => {
                                    const isUser = m.role === "user";
                                    return (
                                      <div
                                        key={m.id || i}
                                        className={`flex flex-col ${
                                          isUser ? "items-end" : "items-start"
                                        }`}
                                      >
                                        <div
                                          className={`max-w-[85%] px-3 py-1.5 text-xs leading-relaxed break-words rounded-xl ${
                                            isUser
                                              ? "bg-orange-500 text-white rounded-br-sm"
                                              : "bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 border border-gray-200 dark:border-gray-600 rounded-bl-sm"
                                          }`}
                                        >
                                          <MessageRenderer
                                            text={m.message}
                                            hideActionLinks
                                          />
                                        </div>
                                        <span className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 px-1">
                                          {fmtClock(m.timestamp)}
                                        </span>
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {userHistory?.hasMore && (
                        <div className="pt-1">
                          <button
                            onClick={onLoadMoreHistory}
                            disabled={userHistory?.loadingMore}
                            className="w-full py-2 rounded-lg text-xs font-medium text-orange-600 dark:text-orange-300 bg-orange-50 dark:bg-orange-500/10 hover:bg-orange-100 dark:hover:bg-orange-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {userHistory?.loadingMore
                              ? "Loading…"
                              : "Load more conversations"}
                          </button>
                          {userHistory?.loadMoreError && (
                            <p className="text-xs text-red-500 mt-1 text-center">
                              {userHistory.loadMoreError}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* thread */
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2.5 bg-gray-50/60 dark:bg-gray-900/30">
                {convLoading ? (
                  <div className="flex flex-col items-center justify-center py-12 gap-3">
                    <div className="flex gap-1.5">
                      {[0, 0.1, 0.2].map((d) => (
                        <span
                          key={d}
                          className="w-2 h-2 bg-orange-500 rounded-full animate-bounce"
                          style={{ animationDelay: `${d}s` }}
                        />
                      ))}
                    </div>
                    <span className="text-xs font-medium text-orange-500">
                      Loading conversation…
                    </span>
                  </div>
                ) : convError ? (
                  <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                    <p className="text-sm font-medium">
                      Couldn't load conversation
                    </p>
                    <p className="text-xs mt-1">{convError}</p>
                    {onRetryConversation && (
                      <button
                        onClick={onRetryConversation}
                        className="mt-3 px-3 py-1.5 text-xs font-medium rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-colors"
                      >
                        Try again
                      </button>
                    )}
                  </div>
                ) : messages.length === 0 ? (
                  <p className="text-center text-sm text-gray-400 py-8">
                    No conversation history found for this session.
                  </p>
                ) : (
                  messages.map((m) => {
                    const flagged = isFlagged(m);
                    const isUser = m.role === "user";
                    return (
                      <div
                        key={m.id}
                        ref={m.id === flaggedAgentId ? flaggedRef : null}
                        className={`flex ${isUser ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[85%] flex flex-col ${isUser ? "items-end" : "items-start"}`}
                        >
                          {flagged && (
                            <span className="mb-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-orange-500">
                              <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />
                              Flagged {isUser ? "question" : "response"}
                            </span>
                          )}
                          <div
                            className={`rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                              flagged
                                ? "ring-2 ring-orange-400 shadow-md " +
                                  (isUser
                                    ? "bg-orange-100 dark:bg-orange-500/20 text-gray-800 dark:text-orange-50"
                                    : "bg-orange-50 dark:bg-orange-500/10 text-gray-800 dark:text-orange-50")
                                : isUser
                                  ? "bg-[#F8EDD8] dark:bg-gray-700 text-gray-800 dark:text-gray-100"
                                  : "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
                            }`}
                          >
                            <p>{m.message}</p>
                            <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
                              {fmtClock(m.time)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* assessment column */}
          <div className="w-[30%] shrink-0 flex flex-col min-h-0 border-t md:border-t-0 border-gray-100 dark:border-gray-700">
            {/* status + category controls */}
            <div className="px-4 py-3.5 border-b border-gray-100 dark:border-gray-700 shrink-0 space-y-3">
              <div>
                <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">
                  Status
                </p>
                <div className="flex items-center gap-1.5">
                  {Object.entries(STATUSES).map(([key, cfg]) => (
                    <button
                      key={key}
                      onClick={() => setDraftStatus(key)}
                      className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                        draftStatus === key
                          ? `${cfg.chip} border-transparent shadow-sm`
                          : "bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-600 hover:border-gray-300"
                      }`}
                    >
                      {cfg.label}
                    </button>
                  ))}
                </div>
              </div>
              {!isGeneral && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">
                    Failure category
                  </p>
                  <select
                    value={draftCategory || ""}
                    onChange={(e) => setDraftCategory(e.target.value || null)}
                    className="w-full px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-colors"
                  >
                    <option value="">Uncategorized</option>
                    {Object.entries(CATEGORIES).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* save bar — only appears once there are staged changes */}
              {dirty && (
                <div className="flex items-center justify-between gap-2 rounded-lg bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/30 px-3 py-2 animate-slideUp">
                  <span className="text-xs font-medium text-orange-700 dark:text-orange-300">
                    Unsaved changes
                  </span>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={discardAssessment}
                      disabled={saving}
                      className="px-2.5 py-1 rounded-lg text-xs font-medium text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-800 disabled:opacity-40 transition-colors"
                    >
                      Discard
                    </button>
                    <button
                      onClick={saveAssessment}
                      disabled={saving}
                      className="px-3 py-1 rounded-lg text-xs font-semibold bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {saving ? "Saving…" : "Save"}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* activity & notes timeline */}
            <div className="flex-1 min-h-0 flex flex-col">
              <p className="px-4 pt-3.5 pb-2 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider shrink-0">
                Activity &amp; notes
              </p>
              <div className="flex-1 overflow-y-auto px-4 space-y-3 pb-3">
                {timeline.length === 0 ? (
                  <p className="text-sm text-gray-400 dark:text-gray-500 py-4">
                    No activity yet. Add the first note below.
                  </p>
                ) : (
                  timeline.map((entry) => (
                    <ActivityEntry key={entry.id} entry={entry} />
                  ))
                )}
              </div>

              {/* add note */}
              <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 shrink-0">
                <div className="flex items-end gap-2">
                  <textarea
                    rows={1}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        submitNote();
                      }
                    }}
                    placeholder={`Note as ${authorName}…`}
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-colors"
                  />
                  <button
                    onClick={submitNote}
                    disabled={!draft.trim()}
                    className="shrink-0 w-9 h-9 rounded-lg bg-orange-500 text-white flex items-center justify-center hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title="Add note (⌘/Ctrl + Enter)"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
